import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { IconProps, List as ListIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Button } from 'theSource/components';
import { ROUTES as SOURCE_ROUTES } from 'theSource/app/routes';
import {
  IconButtonStyled,
  MenuWrapperStyled,
  DrawerMobileVisibleStyled,
} from './EnrollmentNavMobileDrawerStyled';
import {
  getStatusIcon,
  findPathForNavItem,
} from '../EnrollmentNavigation/EnrollmentNavigation';
import {
  selectTotalSubNavItems,
  selectMaxSubNavItem,
} from 'enrollment/components/BottomNavigation/services';
import { selectHeaderItems } from '../EnrollmentNavigation/services';
import { PsButtonTuner, PsModalTuner } from 'stencilSetup';
import { queryClient } from 'theSource/utils';
import { GET_ENROLLMENT_CONTEXT_QUERY_KEY } from 'theSource/queries';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { PSDialog } from 'enrollment/utils/common/Utils';

export interface EnrollmentNavMobileDrawerProps {
  maxNavigationItemIndex: number;
  activeEnrollmentNavigationItemIndex: number;
}

export const EnrollmentNavMobileDrawer = ({
  maxNavigationItemIndex,
  activeEnrollmentNavigationItemIndex,
}: EnrollmentNavMobileDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('navigation.enrollment');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const navTopath = (route: string) => {
    closeDrawer();
    navigate(`enrollment/${route}`);
  };

  const iconProps: IconProps = {
    size: 24,
    color: theme.palette.primary.main,
    weight: 'bold',
  };

  const closeBtnOtherData = [
    { attribute: 'data-bs-toggle', value: 'modal' },
    { attribute: 'data-bs-target', value: '#closeEnrollment-mobile-popover' },
    { attribute: 'data-bs-dismiss', value: 'modal' },
  ];

  const navClose = () => {
    //required for orbit drawer when navigating to a new page
    PSDialog.dialogClose('enrollment-navigation-drawer', () => {
      setIsOpen(false);
      queryClient.invalidateQueries(GET_ENROLLMENT_CONTEXT_QUERY_KEY);
      navigate(SOURCE_ROUTES.main.home.path);
    });
  };

  const toggleDrawer = () => setIsOpen(!isOpen);
  const closeDrawer = () => setIsOpen(false);

  const navItems = useSelector(selectHeaderItems);

  const subNavItems = useSelector(selectTotalSubNavItems);
  const currentSubNavItem = useSelector(selectMaxSubNavItem);

  useEffect(
    () =>
      PSDialog.dialogClose('enrollment-navigation-drawer', () =>
        setIsOpen(false)
      ),
    [location]
  );

  return (
    <DrawerMobileVisibleStyled>
      <IconButtonStyled
        data-testid="drawer-open-button"
        onClick={toggleDrawer}
        aria-haspopup="true"
        aria-expanded="false"
      >
        <ListIcon {...iconProps} aria-hidden="true" role="presentation" />
        <span className="sr-only">{t('common:openMenu')}</span>
      </IconButtonStyled>

      <OrbitDrawer
        aria-label="enrollment-navigation-drawer"
        drawerId="enrollment-navigation-drawer"
        heading={t('title')}
        isOpen={isOpen}
        handleClose={closeDrawer}
        footer={false}
        keepMounted={true}
      >
        <MenuWrapperStyled>
          <div className="card p-3">
            {navItems.map((heading, index) => (
              <div key={`main-nav-${index}`}>
                <Button
                  key={index}
                  id={`navigate-btn-${t(heading)}`}
                  className="btn btn-link overline2 px-1"
                  data-testid="navigate-button"
                  disabled={maxNavigationItemIndex < index}
                  startIcon={getStatusIcon(
                    index,
                    maxNavigationItemIndex,
                    activeEnrollmentNavigationItemIndex
                  )}
                  label={t('nav.'.concat(heading))}
                  variant="tertiary"
                  type="button"
                  onClick={() => navTopath(findPathForNavItem(heading))}
                />
                {activeEnrollmentNavigationItemIndex === index && (
                  <div className="border-left mt-1 pb-1 ml-2_5 pl-2">
                    {subNavItems &&
                      subNavItems.map(
                        (subitem, index) =>
                          subitem &&
                          heading !== 'review' && (
                            <div key={`subnav-mobile-${subitem}`}>
                              <Button
                                className="m-0 px-1 font-weight-normal"
                                id={`subnav-button-${subitem}`}
                                data-testid={`subnav-button-${subitem}`}
                                label={
                                  heading === 'benefits'
                                    ? subitem === 'benefit-details'
                                      ? 'benefitDetails'
                                      : subitem
                                    : t(
                                        `navigation.enrollment:${heading}.${subitem}`
                                      )
                                }
                                variant="tertiary"
                                type="button"
                                disabled={
                                  index < currentSubNavItem ? false : true
                                }
                                onClick={() =>
                                  navTopath(
                                    heading === 'benefits' ? heading : subitem
                                  )
                                }
                              />
                            </div>
                          )
                      )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <PsButtonTuner
              buttonId="close-icon-button"
              label={t('common:closeEnrollment')}
              level="tertiary"
              icon="x-circle ph-duotone"
              otherData={closeBtnOtherData}
            />
            <div
              id="close-notice"
              className="blue-alert my-2"
              data-testid="close-notice"
            >
              {t('common:closeNotice')}
            </div>
          </div>
          <PsModalTuner
            data-testid="closeEnrollment-mobile-popover"
            modalId="closeEnrollment-mobile-popover"
            modalTitle={t('closeEnrollment')}
            primaryActive={true}
            primaryButton={t('saveAndClose')}
            secondaryButton={t('common:cancel')}
            onPsModalSubmit={navClose}
            extraClass="text-left"
          >
            {t('closeNoticePopup')}
          </PsModalTuner>
        </MenuWrapperStyled>
      </OrbitDrawer>
    </DrawerMobileVisibleStyled>
  );
};
